import { useState, useEffect, useCallback } from 'react';

import ErrorHandler, { getErrorMessage } from 'libs/errors/errors';

const ABORT_ERROR_NAME = 'AbortError';

export default function useRequest({ onError } = {}) {
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState(new AbortController());

  const request = useCallback(
    async (requestFunction, args = []) => {
      try {
        setLoading(true);
        setAbortController(new AbortController());

        const result = await requestFunction(...args, { signal: abortController.signal });

        return [true, result];
      } catch (error) {
        const { name: errorName } = error;

        if (errorName === ABORT_ERROR_NAME) {
          return [false, null];
        }

        ErrorHandler.capture(error);
        const errorMessage = getErrorMessage(error);

        if (onError) {
          onError(error);
        }

        return [false, errorMessage];
      } finally {
        setLoading(false);
      }
    },
    [abortController, onError],
  );

  const withRequest = useCallback(
    (requestFunction) => {
      return async (...args) => request(requestFunction, args);
    },
    [request],
  );

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, [abortController]);

  return { loading, request, withRequest };
}
